<template>
  <div class="alert-wrapper">
    <v-alert
      v-if="showWarning"
      density="compact"
      class="alert"
      :class="{ urgent: isUrgent }"
      :closable="!isUrgent">
      <div class="d-flex justify-space-between align-center">
        <span class="d-flex ga-1">
          <v-icon size="small">$warning</v-icon>
          Your free trial ends in
          <span>{{ padUnit(remainingTime.days) }}d </span>
          <span>{{ padUnit(remainingTime.hours) }}h </span>
          <span>{{ padUnit(remainingTime.minutes) }}m </span>
          <span>{{ padUnit(remainingTime.seconds) }}s </span>
          <template v-if="isSubscriptionAdmin && !hasCardOnFile && !activeSubscription?.subscriptionDetails?.cancelAt">
            -
            <RouterLink to="/subscription/billing/manage-billing">add a payment method</RouterLink>
            to keep full access.
          </template>
        </span>
        <span
          class="d-inline-flex ga-1"
          v-if="isSubscriptionAdmin && hasCardOnFile && !!activeSubscription?.subscriptionDetails?.cancelAt && !activeSubscription?.scheduleSubscriptionDetails"
        >
          <RouterLink to="/subscription/my-plan?openChangePlan=true">
            Upgrade now
          </RouterLink> to avoid interruption!
        </span>
      </div>
    </v-alert>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { hasCardOnFile } from '@/utils/payments'

export default {
  data () {
    return {
      showWarning: false,
      remainingTime: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0
      },
      timer: null
    }
  },
  computed: {
    ...mapState({
      userId: state => state.userId,
      currentUser: state => state.user.user,
      activeSubscription: (state) => state.payments.activeSubscription,
      isSubscriptionAdmin: state => state.payments.isSubscriptionAdmin
    }),
    nextBillingDate () {
      const nextBillingDate = this.activeSubscription?.subscriptionDetails?.nextBillingDate
      if (nextBillingDate) {
        return new Date(nextBillingDate)
      }
      return null
    },
    hasCardOnFile () {
      return hasCardOnFile(this.activeSubscription)
    },
    isUrgent () {
      return this.showWarning && this.remainingTime.days < 3
    }
  },
  watch: {
    activeSubscription (activeSubscription) {
      if (!!activeSubscription?.subscriptionDetails && activeSubscription.subscriptionDetails.status === 'trialing' && this.nextBillingDate) {
        this.showWarning = true
        this.updateRemainingTime()
        this.startTimer()
      } else {
        this.showWarning = false
        this.stopTimer()
      }
    }
  },
  methods: {
    updateRemainingTime () {
      if (this.nextBillingDate) {
        const now = new Date()
        const diff = this.nextBillingDate - now

        if (diff <= 0) {
          this.stopTimer()
          this.showWarning = false
          return
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        this.remainingTime = { days, hours, minutes, seconds }
      } else {
        this.remainingTime = { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }
    },
    startTimer () {
      this.stopTimer()
      this.timer = setInterval(this.updateRemainingTime, 1000)
    },
    stopTimer () {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    padUnit (value) {
      return String(value).padStart(2, '0')
    }
  },
  destroyed () {
    this.stopTimer()
  }
}
</script>

<style scoped>
.alert-wrapper {
  width: 100vw;
  margin-left: -40px;
}

.alert {
  font-size: 14px;
  border-radius: 0;
  background-color: #73E5A6;
  font-variant-numeric: tabular-nums;
}

.alert.urgent {
  font-weight: 600;
  background-color: #FFB020;
}

[data-theme="dark"] .alert,
[data-theme="dark"] .alert :deep(.v-alert__content),
[data-theme="dark"] .alert :deep(.v-icon),
[data-theme="dark"] .alert :deep(a) {
  color: #1C1F1D !important;
}

.v-alert__icon {
  font-size: 16px;
}
</style>
